import React, { useEffect, useState } from 'react';
import {
    Box,
    Button,
    Chip,
    IconButton,
    Tooltip,
    Typography,
} from '@mui/material';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { toast } from 'react-toastify';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useHistory, useRouteMatch } from 'react-router-dom';
import DataService from '../../DataService';

const STATUS_META = {
    '-4': { label: 'Feedback Received',  color: '#1565c0', bg: '#e3f2fd' },
    '-2': { label: 'Awaiting Feedback',  color: '#e65100', bg: '#fff3e0' },
    '-1': { label: 'Done',               color: '#555',    bg: '#f5f5f5' },
     '0': { label: 'Created',            color: '#777',    bg: '#fafafa' },
     '1': { label: 'Completed',          color: '#2e7d32', bg: '#e8f5e9' },
};

function StatusChip({ status }) {
    const s = STATUS_META[String(status)] ?? { label: String(status ?? '—'), color: '#555', bg: '#f5f5f5' };
    return (
        <Chip
            label={s.label}
            size="small"
            sx={{ backgroundColor: s.bg, color: s.color, fontWeight: 600, fontSize: '0.72rem' }}
        />
    );
}

const columns = (onReview) => [
    {
        field: 'status',
        headerName: 'Status',
        width: 180,
        renderCell: p => <StatusChip status={p.value} />,
    },
    {
        field: 'name',
        headerName: 'Name',
        flex: 1,
        minWidth: 200,
    },
    {
        field: 'dateEntered',
        headerName: 'Created',
        width: 160,
        type: 'date',
        valueGetter: p => p.row.dateEntered ? new Date(p.row.dateEntered) : null,
        renderCell: p => p.row.dateEntered
            ? new Date(p.row.dateEntered).toISOString().replace('T', ' ').slice(0, 16)
            : '',
    },
    {
        field: 'actions',
        headerName: '',
        width: 110,
        sortable: false,
        renderCell: p => (
            <Button
                size="small"
                variant="outlined"
                onClick={e => { e.stopPropagation(); onReview(p.row); }}
            >
                Review
            </Button>
        ),
    },
];

export default function ClientFeedbackContainer() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [reload, setReload] = useState(false);
    const history = useHistory();
    const { url } = useRouteMatch();

    useEffect(() => {
        setLoading(true);
        DataService.getAuditList()
            .then(data => setRows(data.map((el, i) => ({ ...el, id: i }))))
            .catch(() => toast.error('Failed to load audit list'))
            .finally(() => setLoading(false));
    }, [reload]);

    const handleReview = (row) => history.push(`${url}/${row.hashId}`, { auditName: row.name });

    return (
        <Box sx={{
            m: 2,
            p: 1,
            borderRadius: 1,
            boxShadow: '0 10px 15px -3px rgba(0,0,0,.1), 0 4px 6px -2px rgba(0,0,0,.05)',
            backgroundColor: 'white',
        }}>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, p: 1, pb: 1.5 }}>
                <Typography variant="h6" sx={{ fontWeight: 600 }}>
                    Logo Audit - Review &amp; Feedback
                </Typography>
                <Tooltip title="Refresh">
                    <IconButton sx={{ ml: 'auto' }} size="small" onClick={() => setReload(r => !r)}>
                        <RefreshIcon />
                    </IconButton>
                </Tooltip>
            </Box>

            <Box sx={{ height: 'calc(100vh - 210px)', minHeight: 340 }}>
                <DataGridPro
                    columns={columns(handleReview)}
                    rows={rows}
                    loading={loading}
                    autoHeight={false}
                    rowHeight={52}
                    onRowClick={p => handleReview(p.row)}
                    sx={{ cursor: 'pointer' }}
                    initialState={{
                        sorting: {
                            sortModel: [
                                { field: 'status', sort: 'asc' },
                                { field: 'dateEntered', sort: 'desc' },
                            ],
                        },
                    }}
                />
            </Box>
        </Box>
    );
}
